import type { ImageSegmenter as ImageSegmenterType } from "@mediapipe/tasks-vision";

import { detectionSource } from "@/lib/face-align";

// On-device person cutout (MediaPipe selfie segmenter) for Studio Mode.

const SEGMENT_MAX_EDGE = 512;
export const PREVIEW_MAX_EDGE = 256;

// Below this share of the frame, treat the shot as empty.
const MIN_PERSON_SHARE = 0.02;

// Confidence fades from black to photo between these; 0.45 drops faint bystanders.
const EDGE_LOW = 0.45;
const EDGE_HIGH = 0.85;

let segmenterPromise: Promise<ImageSegmenterType | null> | null = null;
const maskPixels = new WeakMap<HTMLCanvasElement, ImageData>();

export function getPersonSegmenter(): Promise<ImageSegmenterType | null> {
  if (!segmenterPromise) {
    segmenterPromise = (async () => {
      try {
        const { ImageSegmenter, FilesetResolver } =
          await import("@mediapipe/tasks-vision");
        const fileset = await FilesetResolver.forVisionTasks("/mediapipe/wasm");
        return await ImageSegmenter.createFromOptions(fileset, {
          baseOptions: {
            modelAssetPath: "/models/selfie_segmenter.tflite",
            delegate: "CPU",
          },
          runningMode: "IMAGE",
          outputConfidenceMasks: true,
          outputCategoryMask: false,
        });
      } catch (err) {
        console.error("Photo background removal unavailable:", err);
        // Retry on the next call.
        segmenterPromise = null;
        return null;
      }
    })();
  }
  return segmenterPromise;
}

// A light/grey checkerboard, the usual way to show "transparent".
let checkerTile: HTMLCanvasElement | null = null;
function checkerPattern(
  context: CanvasRenderingContext2D,
): CanvasPattern | null {
  if (!checkerTile) {
    checkerTile = document.createElement("canvas");
    checkerTile.width = 8;
    checkerTile.height = 8;
    const tile = checkerTile.getContext("2d");
    if (tile) {
      tile.fillStyle = "#ffffff";
      tile.fillRect(0, 0, 8, 8);
      tile.fillStyle = "#d4d4d4";
      tile.fillRect(0, 0, 4, 4);
      tile.fillRect(4, 4, 4, 4);
    }
  }
  return context.createPattern(checkerTile, "repeat");
}

// What the background layer is painted with: black when omitted, a CSS color,
// or "checker" for a transparent backdrop.
export type BackgroundFill = string | "checker";

// Draws the background layer into `target`; returns the person's share of the frame.
export function drawBackgroundMask(
  segmenter: ImageSegmenterType,
  source: HTMLVideoElement | HTMLCanvasElement,
  target: HTMLCanvasElement,
  maxEdge = SEGMENT_MAX_EDGE,
  fill?: BackgroundFill,
): number | null {
  let result;
  try {
    result = segmenter.segment(detectionSource(source, maxEdge));
  } catch {
    return null;
  }
  try {
    const mask = result.confidenceMasks?.[0];
    if (!mask) return null;
    const { width, height } = mask;
    const confidence = mask.getAsFloat32Array();
    if (target.width !== width) target.width = width;
    if (target.height !== height) target.height = height;
    const context = target.getContext("2d");
    if (!context) return null;
    let pixels = maskPixels.get(target);
    if (!pixels || pixels.width !== width || pixels.height !== height) {
      pixels = context.createImageData(width, height);
      maskPixels.set(target, pixels);
    }
    let person = 0;
    for (let i = 0; i < confidence.length; i++) {
      const t = Math.min(
        Math.max((confidence[i] - EDGE_LOW) / (EDGE_HIGH - EDGE_LOW), 0),
        1,
      );
      const personWeight = t * t * (3 - 2 * t);
      person += personWeight;
      pixels.data[i * 4 + 3] = Math.round((1 - personWeight) * 255);
    }
    context.putImageData(pixels, 0, 0);
    if (fill) {
      // Recolor the layer, keeping its alpha (the cutout's soft edge).
      const style = fill === "checker" ? checkerPattern(context) : fill;
      if (style) {
        context.globalCompositeOperation = "source-in";
        context.fillStyle = style;
        context.fillRect(0, 0, width, height);
        context.globalCompositeOperation = "source-over";
      }
    }
    return person / confidence.length;
  } finally {
    result.close();
  }
}

export function hasPerson(personShare: number | null): boolean {
  return personShare !== null && personShare >= MIN_PERSON_SHARE;
}

// Studio Mode: the person cutout as a PNG the server stores next to the photo.
// Grey value in R=G=B (255 = person, 0 = background), fully opaque, at the
// segmenter's resolution (~512px); the server stretches it over the photo. The
// photo's own pixels are never touched. Null when nobody is in the frame.
export async function computePersonMask(
  segmenter: ImageSegmenterType,
  canvas: HTMLCanvasElement,
): Promise<Blob | null> {
  const backgroundCanvas = document.createElement("canvas");
  const personShare = drawBackgroundMask(segmenter, canvas, backgroundCanvas);
  if (!hasPerson(personShare)) return null;
  const { width, height } = backgroundCanvas;
  const background = backgroundCanvas
    .getContext("2d")
    ?.getImageData(0, 0, width, height);
  if (!background) return null;

  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = width;
  maskCanvas.height = height;
  const context = maskCanvas.getContext("2d");
  if (!context) return null;
  const mask = context.createImageData(width, height);
  for (let i = 0; i < background.data.length; i += 4) {
    const person = 255 - background.data[i + 3];
    mask.data[i] = person;
    mask.data[i + 1] = person;
    mask.data[i + 2] = person;
    mask.data[i + 3] = 255;
  }
  context.putImageData(mask, 0, 0);
  return await new Promise<Blob | null>((resolve) =>
    maskCanvas.toBlob((blob) => resolve(blob), "image/png"),
  );
}

// Same cutout for a saved photo. The copy is scaled down first: the segmenter
// only looks at ~512px, and the aspect ratio is all the mask has to match.
export async function computePersonMaskForImage(
  image: HTMLImageElement,
): Promise<Blob | null> {
  const segmenter = await getPersonSegmenter();
  if (!segmenter || !image.naturalWidth || !image.naturalHeight) return null;
  const scale = Math.min(
    1,
    1024 / Math.max(image.naturalWidth, image.naturalHeight),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return await computePersonMask(segmenter, canvas);
}
